import React, { useState, useMemo } from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
    ReferenceDot,
} from 'recharts';
import { MultiColumnVizProps, CombinedMetadata, getRowValue, useChartTheme } from './types';
import { dsVar } from '../../theme/tokens';
import SentimentSplitTable from './SentimentSplitTable';
import ScatterBubbleChart from './ScatterBubbleChart';
import MultiColumnTable from './MultiColumnTable';
import NetworkGraphVisualization from './NetworkGraphVisualization';
import SankeyChart from './sankey/SankeyChart';

const _findClosest = (
    pts: { x: number; y: number }[],
    tx: number
): { x: number; y: number } | null => {
    if (!pts.length) return null;
    return pts.reduce((p, c) => (Math.abs(c.x - tx) < Math.abs(p.x - tx) ? c : p));
};

const _fmt = (v: number | null | undefined, d = 2): string => {
    if (v === null || v === undefined || isNaN(Number(v))) return 'N/A';
    return Number(v).toFixed(d);
};

const WhatIfTooltip = ({ active, payload, label, xLabel, yLabel }: any) => {
    const theme = useChartTheme();
    if (!active || !payload?.length) return null;
    return (
        <div style={{ background: theme.bg.card, border: `1px solid ${theme.border.default}`, borderRadius: 10, padding: '10px 14px', fontSize: 12, fontFamily: '"Inter", sans-serif' }}>
            <p style={{ color: theme.text.accent, fontWeight: 700, marginBottom: 6 }}>Data Point</p>
            <div style={{ color: theme.text.muted }}>{xLabel}: <span style={{ color: theme.text.body }}>{_fmt(label)}</span></div>
            <div style={{ color: theme.text.muted }}>{yLabel}: <span style={{ color: theme.text.body }}>{_fmt(payload[0]?.value)}</span></div>
        </div>
    );
};

const WhatIfChart: React.FC<MultiColumnVizProps> = ({ data, metadata, itemLabel }) => {
    const theme = useChartTheme();
    const { title, x_axis_label, y_axis_label, x_axis_param, y_axis_param, slider, baseline, baseline_metrics } = metadata;
    const xLabel = x_axis_label || 'X';
    const yLabel = y_axis_label || 'Y';

    const chartPoints = useMemo<{ x: number; y: number }[]>(() => {
        return data.map((row: any) => {
            const x = getRowValue(row, xLabel, x_axis_param, 0);
            const y = getRowValue(row, yLabel, y_axis_param, 1);
            if (x === null || y === null) return null;
            return { x: Number(x), y: Number(y) };
        }).filter(Boolean) as { x: number; y: number }[];
    }, [data, xLabel, yLabel, x_axis_param, y_axis_param]);

    const sliderMin = slider?.min ?? (chartPoints.length ? Math.min(...chartPoints.map(p => p.x)) : 0);
    const sliderMax = slider?.max ?? (chartPoints.length ? Math.max(...chartPoints.map(p => p.x)) : 1);
    const sliderStep = slider?.step ?? 0.1;
    const baselineX = baseline?.x_value;
    const baselineY = baseline?.y_value;
    const sliderDefault = slider?.default ?? baselineX ?? sliderMin;

    const [sv, setSv] = useState<number>(sliderDefault);
    const projected = _findClosest(chartPoints, sv);
    const projectedY = projected?.y ?? null;
    const deltaAbs = projectedY !== null && baselineY !== undefined ? projectedY - baselineY : null;
    const deltaPct = deltaAbs !== null && baselineY ? (deltaAbs / baselineY) * 100 : null;
    const isPos = deltaAbs !== null ? deltaAbs >= 0 : true;

    const metrics = [
        { label: 'Correlation', value: _fmt(baseline_metrics?.correlation) },
        { label: 'Model Type', value: baseline_metrics?.model_type || metadata.model?.type || 'N/A' },
        { label: 'Current Performance', value: _fmt(baseline_metrics?.current_outcome_value) },
    ];

    return (
        <div style={{ background: theme.bg.card, border: `1px solid ${theme.border.default}`, borderRadius: 14, padding: '24px 28px', marginBottom: 32 }}>
            {(title || itemLabel) && <h3 style={{ color: dsVar('accent-fg'), fontSize: 15, fontWeight: 700, marginBottom: 16 }}>{title || itemLabel}</h3>}
            <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: dsVar('success'), background: dsVar('success', 0.1), border: `1px solid ${dsVar('success', 0.3)}`, borderRadius: 4, padding: '2px 8px', marginBottom: 20, fontFamily: '"Inter", sans-serif' }}>
                Simulator Mode Active
            </span>
            <div style={{ display: 'flex', gap: 24 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ height: 300, width: '100%' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartPoints} margin={{ top: 16, right: 24, bottom: 36, left: 24 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={theme.border.default} vertical={false} />
                                <XAxis dataKey="x" type="number" domain={['auto', 'auto']} stroke={theme.text.muted} fontSize={11} tick={{ fontFamily: '"Inter", sans-serif' }}
                                    label={{ value: xLabel, position: 'bottom', fill: theme.text.muted, fontSize: 11, offset: 12, fontFamily: '"Inter", sans-serif' }} />
                                <YAxis dataKey="y" type="number" domain={['auto', 'auto']} stroke={theme.text.muted} fontSize={11} tick={{ fontFamily: '"Inter", sans-serif' }}
                                    label={{ value: yLabel, angle: -90, position: 'insideLeft', fill: theme.text.muted, fontSize: 11, fontFamily: '"Inter", sans-serif' }} />
                                <Tooltip content={<WhatIfTooltip xLabel={xLabel} yLabel={yLabel} />} />
                                <Line type="monotone" dataKey="y" stroke={theme.text.accent} strokeWidth={2.5} dot={false} activeDot={{ r: 5, fill: theme.text.accent }} />
                                {baselineX !== undefined && <ReferenceLine x={baselineX} stroke={theme.baseline} strokeDasharray="5 4" label={{ value: 'Current', position: 'top', fill: theme.baseline, fontSize: 10 }} />}
                                {projected && <ReferenceDot x={projected.x} y={projected.y} r={8} fill={isPos ? theme.sentiment.positive : theme.sentiment.negative} stroke="#fff" strokeWidth={2} />}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                    <div style={{ marginTop: 16, padding: '0 8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontFamily: '"Inter", sans-serif' }}>
                            <span style={{ fontSize: 12, color: theme.text.muted }}>{xLabel}: <strong style={{ color: theme.text.accent }}>{_fmt(sv)}</strong></span>
                            {projectedY !== null && <span style={{ fontSize: 12, color: theme.text.muted }}>{yLabel}: <strong style={{ color: theme.text.body }}>{_fmt(projectedY)}</strong></span>}
                        </div>
                        <input type="range" min={sliderMin} max={sliderMax} step={sliderStep} value={sv}
                            onChange={e => setSv(Number(e.target.value))}
                            style={{ width: '100%', accentColor: theme.text.accent, cursor: 'pointer' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontFamily: '"Inter", sans-serif' }}>
                            <span style={{ fontSize: 10, color: theme.text.muted }}>{_fmt(sliderMin)}</span>
                            <span style={{ fontSize: 10, color: theme.text.muted }}>{_fmt(sliderMax)}</span>
                        </div>
                    </div>
                    {deltaAbs !== null && (
                        <div style={{ marginTop: 14, padding: '12px 16px', background: theme.bg.deep, borderRadius: 10, border: `1px solid ${theme.border.default}`, display: 'flex', gap: 32, fontFamily: '"Inter", sans-serif' }}>
                            <div>
                                <div style={{ fontSize: 10, color: theme.text.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Baseline</div>
                                <div style={{ color: dsVar('info'), fontWeight: 700, fontSize: 16 }}>{_fmt(baselineY)}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: 10, color: theme.text.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Projected</div>
                                <div style={{ color: theme.text.body, fontWeight: 700, fontSize: 16 }}>{_fmt(projectedY)}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: 10, color: theme.text.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Change</div>
                                <div style={{ color: isPos ? dsVar('success') : dsVar('danger'), fontWeight: 700, fontSize: 16 }}>
                                    {deltaAbs >= 0 ? '+' : ''}{_fmt(deltaAbs)}
                                    {deltaPct !== null && <span style={{ fontSize: 11, marginLeft: 6, opacity: 0.8 }}>({deltaPct >= 0 ? '+' : ''}{_fmt(deltaPct, 1)}%)</span>}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div style={{ width: 160, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10, fontFamily: '"Inter", sans-serif' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: theme.text.muted, marginBottom: 4 }}>Key Metrics</div>
                    {metrics.map(({ label, value }) => (
                        <div key={label} style={{ background: theme.bg.deep, border: `1px solid ${theme.border.default}`, borderRadius: 10, padding: '10px 12px' }}>
                            <div style={{ fontSize: 10, color: theme.text.muted, marginBottom: 4, fontFamily: '"Inter", sans-serif' }}>{label}</div>
                            <div style={{ color: theme.text.accent, fontWeight: 700, fontSize: 14, wordBreak: 'break-word' }}>{value}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ─── Resolver ─────────────────────────────────────────────────────────────────

/**
 * VisualizationResolver — plug-and-play dispatcher
 *
 * Routes to the correct visualization component using:
 *  1. metadata.chart_type   (e.g. WHATIF_ANALYSIS)
 *  2. metadata.visualizationType / visualizationtype
 */
const VisualizationResolver: React.FC<MultiColumnVizProps> = (props) => {
    const { metadata } = props;

    const chartType = (metadata.chart_type || '').toUpperCase();
    if (chartType === 'WHATIF_ANALYSIS') return <WhatIfChart {...props} />;

    const visType = (metadata.visualizationType || metadata.visualizationtype || '').toUpperCase();
    switch (visType) {
        case 'NETWORK_GRAPH': return <NetworkGraphVisualization {...props} />;
        case 'SENTIMENT_SPLIT_TABLE': return <SentimentSplitTable {...props} />;
        case 'SCATTER_BUBBLE': return <ScatterBubbleChart {...props} />;
        case 'SANKEY': return <SankeyChart {...props} />;
        default: return <MultiColumnTable {...props} />;
    }
};

export default VisualizationResolver;
export type { MultiColumnVizProps, CombinedMetadata };
